import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { uploadImage, uploadVideo } from "@/api/upload";
import {
  addEventMedia,
  listEventMedia,
  moderateEventMedia,
  removeEventMedia,
  type EventMediaScope,
} from "@/api/event-media";
import { AlbumFromMatches } from "./AlbumFromMatches";
import { MediaReviewToggle } from "./MediaReviewToggle";

/**
 * The event's wall, as the signed-in member may see it. Adding uploads the
 * file with category=event and files it; the server decides from when the
 * file says it was taken whether it goes up at once or waits.
 */
export function EventMediaPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<EventMediaScope>("all");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data, error } = useQuery({
    queryKey: ["event-media", eventId, scope],
    queryFn: () => listEventMedia(eventId, scope),
  });
  const items = data?.data.items ?? [];

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(done);
      await qc.invalidateQueries({ queryKey: ["event-media", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  }

  function add(file: File) {
    void run(async () => {
      const upload = file.type.startsWith("video/") ? await uploadVideo(file, "event") : await uploadImage(file, "event");
      const added = await addEventMedia(eventId, upload.id);
      setMsg(added.data.status === "waiting" ? "Added; it waits for an organiser." : "Added to the wall.");
    }, "Added.");
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">The wall</h3>
      <AlbumFromMatches eventId={eventId} />
      <MediaReviewToggle eventId={eventId} />
      <div className="flex gap-2 flex-wrap items-center text-sm">
        {(["all", "picks", "mine", "waiting", "matches"] as const).map((s) => (
          <button key={s} className={`px-2 py-1 rounded border ${s === scope ? "border-primary-400" : "border-gray-200"}`} onClick={() => setScope(s)}>
            {s}
          </button>
        ))}
        <label className="text-xs text-gray-600">
          Add a photo or video{" "}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) add(file);
            }}
          />
        </label>
      </div>
      {error && <p className="text-xs text-red-600">{(error as Error).message}</p>}
      <ul className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map((m) => (
          <li key={m.id} className="border border-gray-200 rounded-lg p-2 text-xs space-y-1">
            {m.mediaType === "video" ? (
              <video src={m.url} className="w-full rounded" controls />
            ) : (
              <img src={m.thumbnailUrl ?? m.url} alt={m.caption ?? ""} className="w-full rounded" />
            )}
            <p>
              {m.author.name} · {m.status} · {m.takenAt ? `taken ${m.takenAt.slice(11, 16)}` : "no time"} · {m.captureSource}
              {m.durationSeconds !== null
                ? ` · ${Math.floor(m.durationSeconds / 60)}:${String(m.durationSeconds % 60).padStart(2, "0")}`
                : ""}
              {m.isPicked ? " · picked" : ""}
              {m.isHidden ? " · hidden" : ""}
              {m.isHeld ? " · held by reports" : ""}
              {m.review ? ` · review: ${m.review}` : ""}
            </p>
            <div className="flex gap-2 flex-wrap">
              {m.isHeld && (
                <button disabled={busy} onClick={() => run(() => moderateEventMedia(eventId, m.id, { held: false }), "Shown again.")}>show again</button>
              )}
              {m.status === "waiting" && (
                <button disabled={busy} onClick={() => run(() => moderateEventMedia(eventId, m.id, { approved: true }), "Approved.")}>approve</button>
              )}
              <button disabled={busy} onClick={() => run(() => moderateEventMedia(eventId, m.id, { hidden: !m.isHidden }), m.isHidden ? "Shown." : "Hidden.")}>
                {m.isHidden ? "show" : "hide"}
              </button>
              <button disabled={busy} onClick={() => run(() => moderateEventMedia(eventId, m.id, { picked: !m.isPicked }), m.isPicked ? "Un-picked." : "Picked.")}>
                {m.isPicked ? "un-pick" : "pick"}
              </button>
              <button disabled={busy} className="text-red-700" onClick={() => run(() => removeEventMedia(eventId, m.id), "Removed from the wall.")}>
                remove
              </button>
            </div>
          </li>
        ))}
      </ul>
      {!items.length && <p className="text-xs text-gray-500">Nothing here in this view.</p>}
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}
